import { useCallback, useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { API_URL } from '../../common/Constants';
import {
  LIVEKIT_WS_URL,
  buildVoiceRoomName,
  buildUserIdentity,
} from '../../config/livekit';

function estimateBotSpeakingMs(text) {
  if (!text) return 4000;
  const plain = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const chars = plain.length;
  return Math.min(28000, Math.max(2500, Math.round(chars * 45)));
}

function attachBotAudioTrack(track, participant) {
  const existing = document.getElementById('livekit-remote-audio');
  if (existing) {
    existing.remove();
  }
  const el = track.attach();
  el.id = 'livekit-remote-audio';
  el.autoplay = true;
  el.volume = 1;
  el.style.display = 'none';
  document.body.appendChild(el);
  const playPromise = el.play();
  if (playPromise) {
    playPromise.catch((e) => {
      console.warn('[Voice] Remote audio autoplay blocked:', e);
    });
  }
  console.log('[Voice] Playing bot audio from', participant?.identity);
  return el;
}

/**
 * LiveKit voice call hook for ChatbotFrame (parallel to WebSocket text chat).
 */
export function useVoiceCall({ agentId, tenantId, sessionId, clientId, onTranscript }) {
  const roomRef = useRef(null);
  const voiceRoomNameRef = useRef(null);
  const prevSessionIdRef = useRef(sessionId);
  const botSpeakingTimerRef = useRef(null);
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const [voiceError, setVoiceError] = useState(null);
  const [isVoiceMuted, setIsVoiceMuted] = useState(false);
  const [isBotSpeaking, setIsBotSpeaking] = useState(false);

  const clearBotSpeakingTimer = useCallback(() => {
    if (botSpeakingTimerRef.current) {
      clearTimeout(botSpeakingTimerRef.current);
      botSpeakingTimerRef.current = null;
    }
  }, []);

  const duckMicWhileBotSpeaks = useCallback((plainOrHtmlText) => {
    const room = roomRef.current;
    if (!room) return;

    clearBotSpeakingTimer();
    setIsBotSpeaking(true);

    room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
    setIsVoiceMuted(true);

    const ms = estimateBotSpeakingMs(plainOrHtmlText);
    botSpeakingTimerRef.current = setTimeout(() => {
      botSpeakingTimerRef.current = null;
      setIsBotSpeaking(false);
      if (roomRef.current === room) {
        room.localParticipant.setMicrophoneEnabled(true).catch(() => {});
        setIsVoiceMuted(false);
      }
    }, ms);
  }, [clearBotSpeakingTimer]);

  /** Called when Spring pushes voice-transcript over the chat WebSocket. */
  const handleVoiceTranscript = useCallback(
    (role, text, extras = {}) => {
      if (!text?.trim()) return;
      if (onTranscript) {
        onTranscript({
          role: role === 'user' ? 'user' : 'bot',
          text,
          ...extras,
        });
      }
      if (role === 'bot') {
        duckMicWhileBotSpeaks(text);
      }
    },
    [onTranscript, duckMicWhileBotSpeaks],
  );

  const releaseVoiceAgent = useCallback(async (roomName) => {
    if (!roomName || !agentId || tenantId == null) {
      return;
    }
    try {
      await fetch(`${API_URL}/public/voice/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room: roomName,
          agentId: String(agentId),
          tenantId: Number(tenantId),
        }),
      });
    } catch (e) {
      console.warn('[Voice] Agent release failed:', e);
    }
  }, [agentId, tenantId]);

  const endVoiceCall = useCallback(async () => {
    clearBotSpeakingTimer();
    setIsBotSpeaking(false);
    const room = roomRef.current;
    const roomName = voiceRoomNameRef.current;
    roomRef.current = null;
    voiceRoomNameRef.current = null;
    const existing = document.getElementById('livekit-remote-audio');
    if (existing) {
      existing.remove();
    }
    if (room) {
      try {
        room.disconnect();
      } catch (e) {
        console.warn('Voice disconnect:', e);
      }
    }
    if (roomName) {
      await releaseVoiceAgent(roomName);
    }
    setVoiceStatus('idle');
    setVoiceError(null);
    setIsVoiceMuted(false);
  }, [clearBotSpeakingTimer, releaseVoiceAgent]);

  const startVoiceCall = useCallback(async () => {
    if (!agentId || !tenantId || !sessionId) {
      setVoiceError('Start a chat session before calling.');
      setVoiceStatus('error');
      return;
    }
    if (voiceStatus === 'connecting' || voiceStatus === 'in_call') {
      return;
    }

    setVoiceStatus('connecting');
    setVoiceError(null);

    const roomName = buildVoiceRoomName(tenantId, agentId, sessionId);
    const identity = buildUserIdentity(clientId);

    try {
      const tokenParams = new URLSearchParams({
        room: roomName,
        identity,
        tenantId: String(tenantId),
        agentId: String(agentId),
        sessionId: String(sessionId),
      });
      const tokenRes = await fetch(
        `${API_URL}/public/livekit/token?${tokenParams.toString()}`
      );
      if (!tokenRes.ok) {
        const err = await tokenRes.json().catch(() => ({}));
        throw new Error(err.error || `Token request failed (${tokenRes.status})`);
      }
      const { token, wsUrl } = await tokenRes.json();
      const livekitUrl = wsUrl || LIVEKIT_WS_URL;

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });
      roomRef.current = room;

      room.on(RoomEvent.Disconnected, () => {
        if (roomRef.current === room) {
          roomRef.current = null;
          setVoiceStatus('idle');
          setIsBotSpeaking(false);
        }
      });

      room.on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
        if (track.kind !== Track.Kind.Audio) {
          return;
        }
        if (participant?.identity?.startsWith('ai-bot')) {
          attachBotAudioTrack(track, participant);
        }
      });

      await room.connect(livekitUrl, token);

      if (typeof room.startAudio === 'function') {
        try {
          await room.startAudio();
        } catch (e) {
          console.warn('[Voice] room.startAudio failed:', e);
        }
      }

      await room.localParticipant.setMicrophoneEnabled(true);

      const dispatchRes = await fetch(`${API_URL}/public/voice/dispatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room: roomName,
          agentId: String(agentId),
          tenantId: Number(tenantId),
          sessionId: String(sessionId),
        }),
      });

      if (!dispatchRes.ok) {
        const err = await dispatchRes.json().catch(() => ({}));
        throw new Error(
          err.error || `Voice agent dispatch failed (${dispatchRes.status})`
        );
      }

      setVoiceStatus('in_call');
      if (onTranscript) {
        onTranscript({
          role: 'system',
          text: 'Voice call connected. Speak clearly, then pause briefly for a reply.',
        });
      }
    } catch (err) {
      console.error('Voice call failed:', err);
      setVoiceError(err.message || 'Failed to start voice call');
      setVoiceStatus('error');
      await endVoiceCall();
    }
  }, [agentId, tenantId, sessionId, clientId, voiceStatus, onTranscript, endVoiceCall]);

  const toggleVoiceMute = useCallback(async () => {
    const room = roomRef.current;
    if (!room || voiceStatus !== 'in_call' || isBotSpeaking) {
      return;
    }
    const next = !isVoiceMuted;
    await room.localParticipant.setMicrophoneEnabled(!next);
    setIsVoiceMuted(next);
  }, [isVoiceMuted, voiceStatus, isBotSpeaking]);

  useEffect(() => {
    const prev = prevSessionIdRef.current;
    prevSessionIdRef.current = sessionId;
    if (prev !== sessionId && (roomRef.current || voiceRoomNameRef.current)) {
      endVoiceCall();
    }
  }, [sessionId, endVoiceCall]);

  useEffect(() => {
    return () => {
      clearBotSpeakingTimer();
      const room = roomRef.current;
      if (room) {
        try {
          room.disconnect();
        } catch (_) {
          /* ignore */
        }
        roomRef.current = null;
      }
      const existing = document.getElementById('livekit-remote-audio');
      if (existing) {
        existing.remove();
      }
    };
  }, [clearBotSpeakingTimer]);

  return {
    voiceStatus,
    voiceError,
    isVoiceMuted,
    isBotSpeaking,
    startVoiceCall,
    endVoiceCall,
    toggleVoiceMute,
    handleVoiceTranscript,
    isInVoiceCall: voiceStatus === 'in_call',
    isVoiceConnecting: voiceStatus === 'connecting',
  };
}
